// src/utils/storage.js
export const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Error getting from localStorage:', error);
      return null;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Error setting to localStorage:', error);
      return false;
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  },

  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }
};

// Token management
export const tokenStorage = {
  getToken: () => {
    return storage.get('auth_token');
  },

  setToken: (token) => {
    return storage.set('auth_token', token);
  },

  removeToken: () => {
    return storage.remove('auth_token');
  },

  isTokenValid: () => {
    const token = tokenStorage.getToken();
    if (!token) return false;

    try {
      // Basic JWT validation - check if token has proper structure
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;

      // Check if token is expired (if exp field exists)
      if (payload.exp && payload.exp < currentTime) {
        tokenStorage.removeToken(); // Remove expired token
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating token:', error);
      tokenStorage.removeToken(); // Remove invalid token
      return false;
    }
  }
};

// User data management
export const userStorage = {
  getUser: () => {
    return storage.get('user_data');
  },

  setUser: (user) => {
    return storage.set('user_data', user);
  },

  removeUser: () => {
    return storage.remove('user_data');
  },

  updateUser: (updates) => {
    const currentUser = userStorage.getUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates };
      return userStorage.setUser(updatedUser);
    }
    return false;
  }
};

// App settings management
export const settingsStorage = {
  getSettings: () => {
    return storage.get('app_settings') || {};
  },

  setSetting: (key, value) => {
    const settings = settingsStorage.getSettings();
    settings[key] = value;
    return storage.set('app_settings', settings);
  },

  getSetting: (key, defaultValue = null) => {
    const settings = settingsStorage.getSettings();
    return settings[key] !== undefined ? settings[key] : defaultValue;
  },

  removeSetting: (key) => {
    const settings = settingsStorage.getSettings();
    delete settings[key];
    return storage.set('app_settings', settings);
  }
};

// Clear all app data
export const clearAllData = () => {
  tokenStorage.removeToken();
  userStorage.removeUser();
  storage.remove('app_settings');
};

// Export default storage object
export default {
  storage,
  tokenStorage,
  userStorage,
  settingsStorage,
  clearAllData
};